import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconButton, Stack, ThemeProvider, Typography } from '@mui/material';

import { useDTicketSubscription } from '@mopla/business-logic';
import { ESubscriptionStatus } from '@mopla/data-models';
import { MplTrans } from '@mopla/i18';
import { passengerThemeV2 } from '@mopla/mui';
import {
	DTicketPageLayout,
	EMlpIconTypes,
	EMplVariantTypes,
	InformationIcon,
	MplButton,
	MplDivider,
	MplIconMessage,
	MplListItem,
	MplRadioButton,
	MplRadioButtonWithDates,
} from '@mopla/ui';

import { usePassengerAppNavigate } from '../../../hooks';

import { DTicketPauseConfirmationModal } from './DTicketPauseConfirmationModal';
import { DTicketPauseErrorModal } from './DTicketPauseErrorModal';
import { DTicketPauseInfoModal } from './DTicketPauseInfoModal';
import { DTicketPauseSuccessModal } from './DTicketPauseSuccessModal';
import { DTicketSubscriptionPauseSkeleton } from './DTicketSubscriptionPauseSkeleton';
import {
	useDTicketSubscriptionPause,
	useDTicketSubscriptionPauseForm,
	useDTicketSubscriptionPauseInfoModal,
} from './hooks';

export const DTicketSubscriptionPause = () => {
	const { t } = useTranslation('dticketSubscriptionPause');
	const { navigateToUserDTicket, navigateToSubscriptionEdit } =
		usePassengerAppNavigate();

	// Preordered or invalid subscriptions cannot be paused — redirect away if a user
	// lands on this page with a preordered or an invalid subscription.
	// Also redirect away if subscription is paused/has scheduled pause AND
	// has a scheduled cancellation — pausing is not an option in that flow.
	// Same when the backend forbids revoking the cancellation (e.g. cancelled by an
	// admin or failed payment): pausing is unavailable, and the entry points are already hidden, so
	// this only covers direct/deep-linked navigation.
	const { subscription } = useDTicketSubscription();
	const isPreordered = subscription?.status === ESubscriptionStatus.PREORDERED;
	const isInvalid = subscription?.status === ESubscriptionStatus.INVALID;
	const hasPauseAndPendingCancel = !!(
		subscription?.pauseStart && subscription?.canceledAt
	);
	const disallowCancelCancellation = !!subscription?.disallowCancelCancellation;
	const shouldRedirect =
		isPreordered ||
		isInvalid ||
		hasPauseAndPendingCancel ||
		disallowCancelCancellation;

	useEffect(() => {
		if (shouldRedirect) {
			navigateToUserDTicket();
		}
	}, [shouldRedirect, navigateToUserDTicket]);

	const {
		control,
		selectedDuration,
		pauseDates,
		isSubmitting,
		onSubmit,
		pauseDatesForInfoMessage: {
			isTodayAfterDeadline,
			deadlineMonth,
			pauseStartMonthBeforeDeadline,
			pauseStartMonthAfterDeadline,
		},
	} = useDTicketSubscriptionPauseForm();

	const {
		isSubscriptionLoading,
		pendingPauseData,
		pendingCancelData,
		isConfirmationModalOpen,
		isSuccessModalOpen,
		isErrorModalOpen,
		isPauseLoading,
		initiatePause,
		confirmPause,
		cancelConfirmation,
		closeSuccessModal,
		retryPause,
	} = useDTicketSubscriptionPause();

	const { hasPendingCancel, cancelDate } = pendingCancelData;

	const { isInfoModalOpen, openInfoModal, closeInfoModal } =
		useDTicketSubscriptionPauseInfoModal();

	const pauseOptions = [
		{ value: '1' as const, label: t('pause_options.one_month'), months: 1 },
		{ value: '2' as const, label: t('pause_options.two_months'), months: 2 },
		{ value: '3' as const, label: t('pause_options.three_months'), months: 3 },
	];

	return (
		<ThemeProvider theme={passengerThemeV2}>
			<DTicketPageLayout
				title={t('page_title')}
				onBack={navigateToUserDTicket}
				scrollable={false}
				dataTestId="dticket-subscription-pause-page"
			>
				{isSubscriptionLoading || shouldRedirect ? (
					<DTicketSubscriptionPauseSkeleton />
				) : (
					<form onSubmit={onSubmit(initiatePause)}>
						<Stack gap={3}>
							{hasPendingCancel && (
								<MplIconMessage
									type={EMlpIconTypes.Warning}
									variant={EMplVariantTypes.Warning}
									message={
										<MplTrans
											i18nKey="dticketSubscriptionPause:pending_cancel_warning"
											values={{ cancelDate }}
										/>
									}
									dataTestid="dticket-subscriptionPause-pendingCancelWarning-iconMessage"
								/>
							)}

							<Stack gap={2}>
								<Stack gap="8px">
									<Stack
										direction="row"
										alignItems="center"
										justifyContent="space-between"
									>
										<Typography variant="h4-sb" color="primary.main">
											{t(
												hasPendingCancel
													? 'section_title_with_cancel'
													: 'section_title'
											)}
										</Typography>
										<IconButton
											sx={{ padding: '2px' }}
											onClick={openInfoModal}
											data-testid="dticket-subscriptionPause-info-button"
										>
											<InformationIcon />
										</IconButton>
									</Stack>
									<Typography variant="l1" color="primary.main">
										{t('section_description')}
									</Typography>
								</Stack>

								<Controller
									name="duration"
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<Stack gap="12px">
											{pauseOptions.map((option) => {
												const isSelected = field.value === option.value;

												if (isSelected && pauseDates) {
													return (
														<MplRadioButtonWithDates
															key={option.value}
															text={option.label}
															checked={true}
															onChange={() => field.onChange(option.value)}
															ticketValidUntilLabel={t('ticket_valid_until')}
															ticketValidUntilDate={
																pauseDates.lastDateBeforePause
															}
															ticketActiveFromLabel={t('ticket_active_from')}
															ticketActiveFromDate={pauseDates.activeFrom}
															data-testid={`dticket-subscriptionPause-pauseOption-${option.value}-radio`}
														/>
													);
												}

												return (
													<MplRadioButton
														key={option.value}
														text={option.label}
														checked={isSelected}
														onChange={() => field.onChange(option.value)}
														data-testid={`dticket-subscriptionPause-pauseOption-${option.value}-radio`}
													/>
												);
											})}
										</Stack>
									)}
								/>

								<MplButton
									type="submit"
									variant="contained"
									color="primary"
									fullWidth
									disabled={!selectedDuration || isSubmitting}
									showLoader={isSubmitting}
									testId="dticket-subscriptionPause-pauseButton-button"
								>
									{t('pause_button')}
								</MplButton>

								{!isTodayAfterDeadline && (
									<MplIconMessage
										type={EMlpIconTypes.Information}
										variant={EMplVariantTypes.Default}
										message={
											<MplTrans
												i18nKey="dticketSubscriptionPause:info_before_deadline"
												values={{
													deadlineMonth,
													pauseStartMonthBeforeDeadline,
												}}
											/>
										}
										dataTestid="dticketSubscriptionPause-setPauseBeforeDeadline-info-iconMessage"
									/>
								)}

								{isTodayAfterDeadline && (
									<MplIconMessage
										type={EMlpIconTypes.Warning}
										variant={EMplVariantTypes.Warning}
										message={
											<MplTrans
												i18nKey="dticketSubscriptionPause:info_after_deadline"
												values={{
													deadlineMonth,
													pauseStartMonthAfterDeadline,
												}}
											/>
										}
										dataTestid="dticketSubscriptionPause-setPauseAfterDeadline-warning-iconMessage"
									/>
								)}
							</Stack>

							{!hasPendingCancel && (
								<>
									<MplDivider message={t('divider_text')} />

									<MplListItem
										text={t('cancel_subscription')}
										onClick={navigateToSubscriptionEdit}
										dataTestid="dticket-subscriptionPause-cancelSubscription-item"
									/>
								</>
							)}
						</Stack>
					</form>
				)}
			</DTicketPageLayout>
			<DTicketPauseInfoModal open={isInfoModalOpen} onClose={closeInfoModal} />
			{pendingPauseData && (
				<DTicketPauseConfirmationModal
					open={isConfirmationModalOpen}
					onConfirm={confirmPause}
					onCancel={cancelConfirmation}
					activeFrom={pendingPauseData.dates.activeFrom}
					isLoading={isPauseLoading}
				/>
			)}
			{pendingPauseData && (
				<DTicketPauseSuccessModal
					open={isSuccessModalOpen}
					onClose={closeSuccessModal}
					activeFromDate={pendingPauseData.dates.activeFrom}
				/>
			)}
			<DTicketPauseErrorModal open={isErrorModalOpen} onRetry={retryPause} />
		</ThemeProvider>
	);
};
